/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ShowsAndMoviesFilterId,
  ShowsAndMoviesGenreId,
  type TShowsAndMoviesFilterMeta,
  type TShowsAndMoviesGenreMeta,
  type TShowsAndMoviesScreenTemplateType,
  ShowsAndMoviesScreenTemplateType
} from "./showsAndMovies.types";
import { ApplicationFeatureArea } from "../../model/enums";

/**
 * True for the tabs that render the genre chip row beneath the tab strip.
 *
 * Per the Figma annotation "Shows and Movies has genres": the two catalogue
 * tabs are browsable by genre, while For You, On Demand and TiVo+ are curated
 * rails with no genre axis. Everything that forks on "is the genre row on
 * screen?" — the layout, the D-pad vertical order, the refetch key — asks here.
 *
 * @param filter - The active catalogue tab.
 * @returns Whether that tab shows the genre chips.
 */
export const hasGenreSubFilters = (filter: ShowsAndMoviesFilterId): boolean => {
  return (
    filter === ShowsAndMoviesFilterId.SHOWS ||
    filter === ShowsAndMoviesFilterId.MOVIES
  );
};

/** Returns the request attribution for the combined Shows & Movies screen. */
export const getShowsAndMoviesFeatureArea = (
  _filter: ShowsAndMoviesFilterId
): ApplicationFeatureArea => ApplicationFeatureArea.SHOWS_AND_MOVIES;

// `screenId` is `/screens/{id}`; `screenName` is a display label like "For You".
const resolveServerScreenId = (screen: {
  screenName: string;
  screenId?: string;
}): string => {
  if (screen.screenId === undefined || screen.screenId.length === 0) {
    return screen.screenName;
  }
  const segments: string[] = screen.screenId.split("/");
  const lastSegment: string | undefined = segments[segments.length - 1];
  return lastSegment === undefined || lastSegment.length === 0
    ? screen.screenName
    : lastSegment;
};

/**
 * Maps a Showflix server screen name to the app's ShowsAndMoviesFilterId enum.
 * Returns undefined if the server name does not correspond to a known filter.
 *
 * @param screenName - The serverside screen identifier.
 * @returns The app enum value, or undefined if no match exists.
 */
export const mapServerScreenNameToFilterId = (
  screenName: string | undefined | null
): ShowsAndMoviesFilterId | undefined => {
  if (!screenName || typeof screenName !== "string") {
    return undefined;
  }
  const lowerName = screenName.toLowerCase();
  if (
    lowerName === "foryou" ||
    lowerName === "for_you" ||
    lowerName === "for-you"
  ) {
    return ShowsAndMoviesFilterId.FOR_YOU;
  }
  if (
    lowerName === "ondemand" ||
    lowerName === "on_demand" ||
    lowerName === "on-demand"
  ) {
    return ShowsAndMoviesFilterId.ON_DEMAND;
  }
  if (lowerName === "shows") {
    return ShowsAndMoviesFilterId.SHOWS;
  }
  if (lowerName === "movies") {
    return ShowsAndMoviesFilterId.MOVIES;
  }
  if (
    lowerName === "tivoplus" ||
    lowerName === "tivo_plus" ||
    lowerName === "tivo-plus"
  ) {
    return ShowsAndMoviesFilterId.TIVO_PLUS;
  }
  return undefined;
};

/**
 * Maps a server-provided genre name to the app's ShowsAndMoviesGenreId enum.
 * Returns undefined if the genre does not correspond to a known value.
 *
 * @param genreId - The server genre identifier.
 * @returns The app enum value, or undefined if no match exists.
 */
export const mapServerGenreIdToGenreId = (
  genreId: string | undefined | null
): ShowsAndMoviesGenreId | undefined => {
  if (!genreId || typeof genreId !== "string") {
    return undefined;
  }
  const lowerGenre = genreId.toLowerCase();
  if (lowerGenre === "all") {
    return ShowsAndMoviesGenreId.ALL;
  }
  if (lowerGenre === "action") {
    return ShowsAndMoviesGenreId.ACTION;
  }
  if (lowerGenre === "drama") {
    return ShowsAndMoviesGenreId.DRAMA;
  }
  if (lowerGenre === "reality") {
    return ShowsAndMoviesGenreId.REALITY;
  }
  if (lowerGenre === "comedy") {
    return ShowsAndMoviesGenreId.COMEDY;
  }
  if (lowerGenre === "kids") {
    return ShowsAndMoviesGenreId.KIDS;
  }
  return undefined;
};

/**
 * Maps server-provided screen metadata to supported catalogue tabs.
 *
 * @param serverScreens - Array of screens from the Showflix API response.
 */
export const mapServerShowflixFilters = (
  serverScreens: ReadonlyArray<{
    screenName: string;
    screenId?: string;
    templateType?: string;
    defaultScreen?: boolean;
  }>
): TShowsAndMoviesFilterMeta[] => {
  const newFilters: TShowsAndMoviesFilterMeta[] = [];
  for (const screen of serverScreens) {
    const filterId = mapServerScreenNameToFilterId(
      resolveServerScreenId(screen)
    );
    if (filterId !== undefined && screen.screenName.length > 0) {
      const filter: TShowsAndMoviesFilterMeta = {
        id: filterId,
        label: screen.screenName
      };
      if (
        screen.templateType ===
          ShowsAndMoviesScreenTemplateType.SINGLE_CAROUSEL_GRID ||
        screen.templateType ===
          ShowsAndMoviesScreenTemplateType.MULTI_CAROUSEL_ROWS
      ) {
        filter.templateType =
          screen.templateType as TShowsAndMoviesScreenTemplateType;
      }
      if (screen.defaultScreen === true) {
        filter.isDefault = true;
      }
      newFilters.push(filter);
    }
  }
  return newFilters;
};

/**
 * Maps API-provided genre metadata for the active genre-bearing screen.
 *
 * @param serverGenres - Array of genres from the active screen's genreList.
 */
export const mapServerShowflixGenres = (
  serverGenres: ReadonlyArray<{ genreId: string; caption?: string }>
): TShowsAndMoviesGenreMeta[] => {
  const newGenres: TShowsAndMoviesGenreMeta[] = [];
  for (const genre of serverGenres) {
    const genreId = mapServerGenreIdToGenreId(genre.genreId);
    if (genreId !== undefined) {
      const label = genre.caption || genre.genreId;
      newGenres.push({ id: genreId, label, apiGenreId: genre.genreId });
    }
  }
  return newGenres;
};
